/**
 * Central registry for application form question types.
 * Used by builder type picker, validation hints, and render-family routing.
 */

export const QUESTION_TYPE_CATEGORIES = [
  { key: 'basic', label: 'Basic inputs' },
  { key: 'numeric', label: 'Numbers & amounts' },
  { key: 'datetime', label: 'Date & time' },
  { key: 'choice', label: 'Choices & selections' },
  { key: 'scale', label: 'Ratings & scales' },
  { key: 'content', label: 'Content & layout' },
  { key: 'legal', label: 'Declarations & consent' },
  { key: 'signature', label: 'Signatures' },
  { key: 'table', label: 'Tables & lists' },
  { key: 'media', label: 'Files & media' },
];

function t(value, label, category, family, hint, example = '', extra = {}) {
  return {
    value,
    label,
    category,
    family,
    hint,
    example,
    contentField: false,
    needsOptions: false,
    ...extra,
  };
}

/** @type {Array<{ value: string, label: string, category: string, family: string, hint: string, example?: string, contentField?: boolean, needsOptions?: boolean }>} */
export const APPLICATION_QUESTION_TYPES = [
  // Basic
  t('TEXT', 'Short Text', 'basic', 'text', 'Single-line text input.', 'e.g. John Smith'),
  t('TEXTAREA', 'Long Text', 'basic', 'textarea', 'Multi-line text area.', 'e.g. achievement summary'),
  t('EMAIL', 'Email', 'basic', 'email', 'Validates email format.', 'john@company.com'),
  t('PHONE', 'Phone', 'basic', 'phone', 'International phone input.', '+94 77 123 4567'),
  t('URL', 'Website / URL', 'basic', 'url', 'URL with validation.', 'https://example.com'),
  t('PASSWORD', 'Password', 'basic', 'password', 'Masked password field.', '••••••••'),
  t('HIDDEN', 'Hidden Field', 'basic', 'hidden', 'Not shown to applicant; stores a fixed/hidden value.', ''),

  // Numeric
  t('NUMBER', 'Number', 'numeric', 'number', 'Whole or decimal number.', '42'),
  t('DECIMAL', 'Decimal', 'numeric', 'number', 'Decimal number with precision.', '99.95'),
  t('CURRENCY', 'Currency', 'numeric', 'currency', 'Money amount with currency symbol.', '1,250.00'),
  t('PERCENTAGE', 'Percentage (%)', 'numeric', 'percentage', 'Numeric % value with configurable max (default 0–100, or unlimited).', '95 %'),
  t('YEAR', 'Year', 'numeric', 'year', 'Four-digit year.', '2025'),
  t('RANGE', 'Number Slider', 'numeric', 'slider', 'Drag to pick a number in a range.', '1 ———●——— 10'),

  // Date & time
  t('DATE', 'Date', 'datetime', 'date', 'Calendar date picker.', '15 Jan 2025'),
  t('TIME', 'Time', 'datetime', 'time', 'Time picker.', '14:30'),
  t('DATETIME', 'Date & Time', 'datetime', 'datetime', 'Combined date and time.', '15 Jan 2025 14:30'),
  t('DATE_RANGE', 'Date Range', 'datetime', 'date-range', 'Start and end dates.', '01 Jan – 31 Dec'),
  t('MONTH', 'Month', 'datetime', 'month', 'Month and year.', 'June 2025'),
  t('WEEK', 'Week', 'datetime', 'week', 'Week selector.', 'Week 24, 2025'),

  // Choices
  t('SINGLE_CHOICE', 'Single Choice (Radio)', 'choice', 'choice-single', 'Pick exactly one option.', '○ A ● B ○ C'),
  t('MULTIPLE_CHOICE', 'Multiple Choice (Checkboxes)', 'choice', 'choice-multi', 'Pick any number of options.', '☑ A ☐ B'),
  t('DROPDOWN', 'Dropdown', 'choice', 'dropdown', 'Select one from a list.', 'Industry ▾'),
  t('YES_NO', 'Yes / No', 'choice', 'yes-no', 'Binary yes or no.', 'Yes | No'),
  t('TRUE_FALSE', 'True / False', 'choice', 'yes-no', 'Boolean true/false.', 'True | False'),
  t('CHECKBOX_SINGLE', 'Single Checkbox', 'choice', 'checkbox-single', 'One agree/checkbox.', '☐ I agree'),
  t('BUTTON_GROUP', 'Button Group', 'choice', 'choice-single', 'Options as toggle buttons.', '[ A ] [ B ]'),
  t('IMAGE_CHOICE', 'Image Choice', 'choice', 'image-choice', 'Choose from image tiles.', 'image thumbnails'),
  t('LIKERT', 'Likert Scale', 'choice', 'likert', 'Agreement scale (Strongly disagree … Strongly agree).', ''),
  t('NPS', 'Net Promoter Score', 'choice', 'nps', '0–10 recommendation score.', '0 … 10'),
  t('RANKING', 'Ranking', 'choice', 'ranking', 'Drag to rank options.', '1st, 2nd, 3rd'),
  t('MATRIX_SINGLE', 'Matrix (one per row)', 'choice', 'matrix-single', 'Grid: one choice per row.', 'rows × columns'),
  t('MATRIX_MULTI', 'Matrix (multi per row)', 'choice', 'matrix-multi', 'Grid: multiple per row.', 'rows × columns'),
  t('SINGLE_CHOICE_OTHER', 'Single Choice with Other', 'choice', 'choice-single-other', 'Radio list with write-in when Other is selected.', '○ A ● Other: ___', { needsOptions: true }),
  t('MULTIPLE_CHOICE_OTHER', 'Multiple Choice with Other', 'choice', 'choice-multi-other', 'Checkboxes with write-in when Other is selected.', '☑ Other: ___', { needsOptions: true }),
  t('DROPDOWN_OTHER', 'Dropdown with Other', 'choice', 'dropdown-other', 'Dropdown with write-in when Other is selected.', 'Select ▾ + specify', { needsOptions: true }),

  // Scales
  t('RATING', 'Star Rating', 'scale', 'rating', '1–N star rating.', '★★★☆☆'),
  t('STAR_RATING_HALF', 'Half-Star Rating', 'scale', 'rating', 'Half-step star rating.', '★★★½☆'),
  t('SLIDER', 'Slider', 'scale', 'slider', 'Continuous slider value.', '——●——'),
  t('SCALE_1_10', 'Scale 1–10', 'scale', 'scale-buttons', 'Pick a number 1 through 10.', '1 2 3 … 10'),
  t('OPINION_SCALE', 'Opinion Scale', 'scale', 'likert', 'Custom labeled scale.', 'Poor … Excellent'),

  // Content & layout
  t('STATIC_TEXT', 'Static Text / Info Block', 'content', 'static', 'Display-only instructions.', 'Please read carefully…', { contentField: true }),
  t('SECTION_HEADER', 'Section Header', 'content', 'section-header', 'Large heading divider.', 'Section title', { contentField: true }),
  t('DIVIDER', 'Divider / Spacer', 'content', 'divider', 'Visual separator only.', ''),
  t('HTML_BLOCK', 'Rich HTML Block', 'content', 'html', 'Formatted HTML content.', '<p>…</p>', { contentField: true }),
  t('INFO_CALLOUT', 'Info Callout', 'content', 'callout', 'Highlighted info box.', 'Important note', { contentField: true }),
  t('TERMS_LINK', 'Terms & Conditions Link', 'content', 'terms-link', 'Link to external terms.', 'View terms', { contentField: true }),
  t('EMBED_VIDEO', 'Embedded Video', 'content', 'embed', 'Embed a video URL.', 'YouTube / Vimeo URL', { contentField: true }),
  t('IMAGE_DISPLAY', 'Display Image', 'content', 'image-display', 'Show an image (no upload).', 'Banner image URL', { contentField: true }),
  t('PIPED_DISPLAY', 'Piped Display (read-only)', 'content', 'piped-display', 'Shows another question\'s answer read-only (e.g. objective from earlier section).', 'Auto-filled from field', { contentField: true }),

  // Legal / declarations
  t('DECLARATION_FORMAT_1', 'Declaration — Print & Sign (Full)', 'legal', 'declaration-print', 'Print/sign block with multiple hand-fill fields.', 'Authorisation letter', { contentField: true }),
  t('DECLARATION_FORMAT_2', 'Declaration — Online Consent', 'legal', 'declaration-consent', 'Checkbox + date captured online.', 'Digital consent', { contentField: true }),
  t('DECLARATION_FORMAT_3', 'Declaration — Print & Sign (Simple)', 'legal', 'declaration-print', 'Print/sign with name and date only.', 'Simple declaration', { contentField: true }),
  t('DECLARATION_CUSTOM', 'Declaration — Custom Fields', 'legal', 'declaration-custom', 'Fully configurable declaration fields.', 'Custom legal block', { contentField: true }),
  t('CONSENT_CHECKBOX', 'Consent Checkbox', 'legal', 'declaration-consent', 'Single consent with optional date.', '☐ I consent', { contentField: true }),
  t('TERMS_ACCEPTANCE', 'Terms Acceptance', 'legal', 'declaration-consent', 'Accept terms with timestamp.', '☐ I accept the terms'),
  t('PRIVACY_NOTICE', 'Privacy Notice Acknowledgment', 'legal', 'declaration-consent', 'Privacy policy acknowledgment.', '☐ I have read the privacy notice', { contentField: true }),

  // Signatures
  t('SIGNATURE_PAD', 'Signature Pad (Draw)', 'signature', 'signature-pad', 'Draw signature on canvas.', 'Sign here'),
  t('SIGNATURE_UPLOAD', 'Signature Upload', 'signature', 'signature-upload', 'Upload image of signature.', 'PNG/JPG signature'),
  t('WITNESS_SIGNATURE', 'Witness Signature', 'signature', 'signature-pad', 'Second signatory block.', 'Witness sign'),
  t('SIGNATURE_DATE', 'Signature + Date', 'signature', 'declaration-print', 'Signature line with date.', 'Sign & date'),

  // Tables & lists
  t('LABELED_LIST', 'Labeled List (a, b, c…)', 'table', 'labeled-list', 'Lettered rows with text inputs.', 'a. ___ b. ___', { needsOptions: true }),
  t('TABLE_ROWS', 'Table Rows (2 columns)', 'table', 'table-rows', 'Lettered rows in a 2-column table.', 'Item | Value', { needsOptions: true }),
  t('DATA_GRID', 'Data Grid (multi-column)', 'table', 'data-grid', 'Configurable column table.', 'Col A | Col B | Col C', { needsOptions: true }),
  t('DROPDOWN_GRID', 'Dropdown Grid (exclusive/unique options)', 'table', 'data-grid', 'Table with dropdown columns and mutually exclusive row options.', 'Role ▾ | Name | Email', { needsOptions: true }),
  t('DYNAMIC_TABLE', 'Dynamic Table (Add/Remove rows)', 'table', 'data-grid', 'Configurable table where applicants can add and remove rows.', 'Name | Role (+ Add row)', { needsOptions: true }),
  t('REPEATING_SECTION', 'Repeating Section', 'table', 'repeating', 'Add/remove row groups.', '+ Add row'),
  t('FIXED_TABLE', 'Fixed Table (read-only layout)', 'table', 'fixed-table', 'Predefined table structure.', 'schedule grid', { contentField: true }),
  t('COMPARISON_TABLE', 'Comparison Table', 'table', 'data-grid', 'Compare options side by side.', 'Feature matrix'),
  t('SIDE_BY_SIDE_CHOICE', 'Side-by-Side Choice Table', 'table', 'side-by-side-choice', 'Pick one option per column (e.g. current year vs prior year).', 'Col A | Col B ranges', { needsOptions: true }),

  // Media
  t('TEXT_WITH_FILE', 'Text with Attachment', 'media', 'text-with-file', 'Text answer and document upload in a single question.', 'Description + supporting PDF'),
  t('FILE', 'File Upload', 'media', 'file', 'Document upload.', 'PDF, DOC, XLS'),
  t('IMAGE', 'Image Upload', 'media', 'image', 'Image file upload.', 'JPG, PNG'),
  t('VIDEO', 'Video Upload', 'media', 'video', 'Video file upload (MP4 format only).', 'MP4 only (.mp4)'),
  t('AUDIO', 'Audio Upload', 'media', 'audio', 'Audio file upload (MP3 format only).', 'MP3 only (.mp3)'),
  t('FILE_MULTI', 'Multiple Files', 'media', 'file', 'Upload several files.', 'multiple documents'),
  t('IMAGE_GALLERY', 'Image Gallery Upload', 'media', 'image', 'Multiple images.', 'photo gallery'),
  t('CAMERA_CAPTURE', 'Camera Capture', 'media', 'camera', 'Capture from device camera.', 'take photo'),
];

const TYPE_BY_VALUE = new Map(APPLICATION_QUESTION_TYPES.map((x) => [x.value, x]));

export function getQuestionTypeMeta(type) {
  const key = String(type || '').toUpperCase();
  return TYPE_BY_VALUE.get(key) || {
    value: key,
    label: key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
    category: 'basic',
    family: 'text',
    hint: 'Unknown question type',
    contentField: false,
    needsOptions: false,
  };
}

export function getQuestionTypeLabel(type) {
  return getQuestionTypeMeta(type).label;
}

export function getRenderFamily(type) {
  return getQuestionTypeMeta(type).family || 'text';
}

export function isContentQuestionType(type) {
  return !!getQuestionTypeMeta(type).contentField;
}

export function isDeclarationFamily(type) {
  const f = getRenderFamily(type);
  return f === 'declaration-print' || f === 'declaration-consent' || f === 'declaration-custom';
}

export function typesNeedingOptions(type) {
  const meta = getQuestionTypeMeta(type);
  if (meta.needsOptions) return true;
  const f = meta.family;
  return ['choice-single', 'choice-multi', 'choice-single-other', 'choice-multi-other', 'dropdown-other', 'dropdown', 'likert', 'nps', 'ranking', 'matrix-single', 'matrix-multi', 'image-choice', 'scale-buttons'].includes(f);
}

export function searchQuestionTypes(query = '') {
  const q = String(query || '').trim().toLowerCase();
  const list = APPLICATION_QUESTION_TYPES;
  if (!q) return list;
  return list.filter((item) => {
    const hay = `${item.label} ${item.value} ${item.hint} ${item.category}`.toLowerCase();
    return hay.includes(q);
  });
}

export function getQuestionTypesByCategory(query = '') {
  const filtered = searchQuestionTypes(query);
  const byCat = new Map();
  for (const cat of QUESTION_TYPE_CATEGORIES) {
    byCat.set(cat.key, []);
  }
  for (const item of filtered) {
    const bucket = byCat.get(item.category) || [];
    bucket.push(item);
    byCat.set(item.category, bucket);
  }
  return QUESTION_TYPE_CATEGORIES
    .map((cat) => ({ ...cat, types: byCat.get(cat.key) || [] }))
    .filter((cat) => cat.types.length > 0);
}

export const ALL_QUESTION_TYPE_VALUES = APPLICATION_QUESTION_TYPES.map((t) => t.value);

export const FAMILY_BY_TYPE = Object.fromEntries(
  APPLICATION_QUESTION_TYPES.map((item) => [item.value, item.family])
);

const NO_PLACEHOLDER_FAMILIES = new Set([
  'rating', 'percentage', 'year', 'labeled-list', 'table-rows', 'data-grid',
  'declaration-print', 'declaration-consent', 'declaration-custom', 'signature-pad',
  'divider', 'static', 'section-header', 'callout', 'html',
  'fixed-table', 'repeating', 'camera', 'likert', 'nps', 'matrix-single', 'matrix-multi', 'ranking',
  'side-by-side-choice',
  'image-choice', 'scale-buttons', 'embed', 'image-display', 'terms-link', 'piped-display',
  'choice-single-other', 'choice-multi-other', 'dropdown-other',
  'signature-upload', 'file', 'image', 'video', 'audio',
]);

/** Builder: show placeholder field for this type */
export function questionSupportsPlaceholder(type) {
  const meta = getQuestionTypeMeta(type);
  if (meta.contentField || meta.needsOptions) return false;
  return !NO_PLACEHOLDER_FAMILIES.has(meta.family);
}

/** Builder: show required checkbox for this type */
export function questionSupportsRequiredToggle(type) {
  const f = getRenderFamily(type);
  if (f === 'declaration-print') return false;
  if (['static', 'section-header', 'divider', 'html', 'callout', 'terms-link', 'embed', 'image-display', 'fixed-table', 'piped-display'].includes(f)) {
    return false;
  }
  return true;
}

/** Types that never collect/store answers (static content blocks only). */
export function isNonAnswerContentType(type) {
  const f = getRenderFamily(type);
  return f === 'static' || f === 'section-header' || f === 'divider' || f === 'html'
    || f === 'callout' || f === 'terms-link' || f === 'embed' || f === 'image-display'
    || f === 'fixed-table' || f === 'piped-display';
}

/** Builder: show min/max word count fields */
export function questionSupportsWordLimits(type) {
  const t = String(type || '').toUpperCase();
  return ['TEXT', 'TEXTAREA', 'TEXT_WITH_FILE'].includes(t);
}

/** Builder: show min/max selection count fields */
export function questionSupportsSelectionLimits(type) {
  const t = String(type || '').toUpperCase();
  return ['MULTIPLE_CHOICE', 'MULTIPLE_CHOICE_OTHER'].includes(t);
}

/** Builder: show visibility rules editor */
export function questionSupportsVisibilityRules(type) {
  return !isNonAnswerContentType(type);
}
